import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Loader2, TriangleAlert } from 'lucide-react'
import type { ProvenanceChain } from '@deepguide-ai/types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Card, CardContent } from '@deepguide-ai/ui/card'
import { LineageDetailsPanel } from './LineageDetailsPanel'
import { LineageViewer } from './LineageViewer'

function getDefaultSelectedNodeId(chain?: ProvenanceChain | null) {
  if (!chain?.nodes.length) return null
  return chain.nodes.find((node) => node.id === chain.rootId)?.id ?? chain.nodes[0]?.id ?? null
}

export function LineageDialog({
  open,
  onOpenChange,
  title,
  description,
  workspaceId,
  chain,
  isLoading,
  error,
  summary,
  emptyStateTitle,
  emptyStateDescription,
  forceEmptyState = false,
  readOnly = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  workspaceId: string
  chain?: ProvenanceChain | null
  isLoading?: boolean
  error?: string | null
  summary?: ReactNode
  emptyStateTitle?: string
  emptyStateDescription?: string
  forceEmptyState?: boolean
  readOnly?: boolean
}) {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setSelectedNodeId(null)
      return
    }

    setSelectedNodeId(getDefaultSelectedNodeId(chain))
  }, [open, chain])

  const selectedNode = useMemo(
    () => chain?.nodes.find((node) => node.id === selectedNodeId) ?? null,
    [chain, selectedNodeId]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[85vh] !max-w-6xl gap-0 overflow-hidden p-0'>
        <DialogHeader className='shrink-0'>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-6'>
          {summary}

          {isLoading ? (
            <div className='flex h-[60vh] items-center justify-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Loading lineage…
            </div>
          ) : error ? (
            <Card className='border-red-200'>
              <CardContent className='flex items-center gap-3 py-6'>
                <TriangleAlert className='h-5 w-5 text-destructive' />
                <div className='text-sm text-status-red'>{error}</div>
              </CardContent>
            </Card>
          ) : forceEmptyState ? (
            <Card>
              <CardContent className='py-6'>
                <div className='text-sm font-medium text-foreground'>
                  {emptyStateTitle ?? 'No lineage available'}
                </div>
                <div className='mt-1 text-sm text-muted-foreground'>
                  {emptyStateDescription ?? 'This record does not have a provenance chain yet.'}
                </div>
              </CardContent>
            </Card>
          ) : chain ? (
            <div className='grid min-h-0 flex-1 grid-cols-2 gap-4'>
              <div className='min-h-0 overflow-hidden'>
                <LineageViewer
                  chain={chain}
                  workspaceId={workspaceId}
                  className='h-[60vh] rounded-md border'
                  emptyStateTitle={emptyStateTitle}
                  emptyStateDescription={emptyStateDescription}
                  selectedNodeId={selectedNodeId ?? undefined}
                  onSelectNode={(node) => {
                    setSelectedNodeId(node.id)
                  }}
                />
              </div>

              <div className='min-h-0 overflow-hidden'>
                <LineageDetailsPanel
                  node={selectedNode}
                  chain={chain}
                  workspaceId={workspaceId}
                  className='h-[60vh]'
                  readOnly={readOnly}
                />
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className='py-6 text-sm text-muted-foreground'>
                No lineage available.
              </CardContent>
            </Card>
          )}
        </div>
   
      </DialogContent>
    </Dialog>
  )
}
